// custom-collection.js — General Collection layout supplement
// Single-select colour filtering and the category tag-filter cards. The grid
// itself lives in sections/product-collection-general.liquid.
//
// Loaded on the collection templates AND on templates/search.json, which renders
// the same section. Everything here rebuilds the URL from window.location and only
// ever touches filter.* and page, so the search page's own q and type survive.
//
// The Quick Add modal and the .pcard colour swatches used to be here too. They
// are shared with the homepage rails now, so they live in
// assets/pcard-quick-add.js, which theme.liquid loads before this file.

// ── COLOUR FILTER: SINGLE SELECT ─────────────────────────────────────────────
// Shopify filters are multi-select by default. Colour should be one at a time, so
// every colour param is cleared before the clicked one is applied.
//
// Deliberately across BOTH colour filters — during the migration there are two
// side by side (the variant option on the older products, the custom.color
// metafield on the split ones). Shopify ANDs separate filters, so holding a value
// in each would always return nothing; clearing both keeps "one colour" honest.
//
// The href is rewritten in the capture phase so that whatever handles the click
// next follows it: the section's AJAX filter handler reads the href on bubble, and
// a plain navigation would use it too.
function csColourFilterParams() {
  const params = new Set();
  document.querySelectorAll('[data-colour-filter]').forEach(el => params.add(el.dataset.colourFilter));
  return params;
}

document.addEventListener('click', event => {
  const link = event.target.closest && event.target.closest('a[data-colour-filter]');
  if (!link) return;

  const url = new URL(link.getAttribute('href'), window.location.origin);
  csColourFilterParams().forEach(param => url.searchParams.delete(param));

  // Clicking the colour that is already on clears it instead of re-applying.
  if (link.dataset.colourActive !== 'true') {
    url.searchParams.set(link.dataset.colourFilter, link.dataset.colourValue);
  }
  url.searchParams.delete('page');

  link.setAttribute('href', url.pathname + url.search);
}, true);

// ── CATEGORY NAV: TAG FILTER CARDS ───────────────────────────────────────────
// Each card in .cat-nav stands for one or more product tags. Shopify ORs multiple
// values of the same filter, so a card carrying two tags returns products matching
// either — something a tag path (/collections/x/a+b) cannot do, being an AND.
//
// Single select: clicking a card replaces whatever tag filter is on, and clicking
// the active card clears it. Every other filter (size, colour, price) and the sort
// survive, because the URL is built from the one currently in the address bar —
// which is why this is done here and not in Liquid.
//
// Capture phase, like the colour filter above: the section's AJAX handler reads the
// href on bubble, and a plain navigation uses it too, so both follow the rewrite.
document.addEventListener('click', event => {
  const card = event.target.closest && event.target.closest('a[data-cat-tags], a[data-clear-filters]');
  if (!card) return;

  const url = new URL(window.location.href);

  if (card.hasAttribute('data-clear-filters')) {
    // The "All" card and both "Clear all" buttons: drop every filter but keep the
    // rest of the query string. The view parameter matters most — losing it drops
    // the page back to the collection's default template.
    Array.from(url.searchParams.keys())
      .filter(key => key.indexOf('filter.') === 0)
      .forEach(key => url.searchParams.delete(key));
  } else {
    url.searchParams.delete('filter.p.tag');
    if (card.dataset.catActive !== 'true') {
      card.dataset.catTags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean)
        .forEach(tag => url.searchParams.append('filter.p.tag', tag));
    }
  }
  url.searchParams.delete('page');

  card.setAttribute('href', url.pathname + url.search);
}, true);

// ── CATEGORY NAV: ACTIVE STATE FROM THE URL ──────────────────────────────────
// Liquid works out which card is active from tag_filter.active_values, and Shopify
// drops values that are not filter values in the collection being viewed. So a card
// whose tags match nothing here comes back with zero active values, and the server
// lights up "All" instead — the shopper clicked Accessories and nothing looks
// selected. Liquid cannot read the query string, so the URL is the only place that
// still knows what was asked for.
function csSyncCatNav() {
  const nav = document.querySelector('.cat-nav');
  if (!nav) return;

  const urlTags = new URLSearchParams(window.location.search)
    .getAll('filter.p.tag')
    .map(tag => tag.trim().toLowerCase())
    .filter(Boolean);

  // No tag in the URL: the server already has it right ("All" is active).
  if (!urlTags.length) return;

  const cards = Array.from(nav.querySelectorAll('.cat-card'));
  const match = cards.find(card => {
    if (!card.dataset.catTags) return false;
    const own = card.dataset.catTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    return urlTags.every(tag => own.indexOf(tag) !== -1);
  });
  if (!match) return; // tags from somewhere else — leave the server's state alone

  cards.forEach(card => {
    card.classList.remove('active');
    if (card.dataset.catTags) card.dataset.catActive = 'false';
  });
  match.classList.add('active');
  match.dataset.catActive = 'true';
}

document.addEventListener('DOMContentLoaded', csSyncCatNav);
window.addEventListener('popstate', csSyncCatNav);

// The section's filter AJAX replaces the contents of #pcg-root, which wipes the
// classes set above — re-apply them whenever it does.
(function watchCatNav() {
  const root = document.getElementById('pcg-root');
  if (!root || typeof MutationObserver !== 'function') return;
  new MutationObserver(csSyncCatNav).observe(root, { childList: true });
})();
